import { ClerkOfflineError } from '@clerk/react-router/errors';
import axios from 'axios';
import { ALERT_VIEW_STATE } from '~/features/xstateSystem';
import {
  SESSION_REFRESH_SUBTEXT,
  SESSION_REFRESH_TEXT,
  isMiddlewareAuthRejection,
  requestSentBearerToken,
} from '~/utils/authErrors';
import type { ServiceError } from './errors';
import type { AlertEvents, AlertPayload } from './types';

// Map HTTP status codes to short, user-facing headlines. Anything not listed
// falls back to a generic "Request failed" with the status code in subText.
const HTTP_STATUS_HEADLINES: Record<number, string> = {
  400: 'Invalid request',
  401: 'Session needs to refresh',
  403: 'Not authorized for this resource',
  404: 'Not found',
  408: 'Request timed out',
  409: 'Conflict with current state',
  422: 'Some fields need attention',
  429: 'Too many requests',
  500: 'Server error',
  502: 'Server temporarily unavailable',
  503: 'Server temporarily unavailable',
  504: 'Server timed out',
};

const formatHttpStatusHeadline = (status: number): string =>
  HTTP_STATUS_HEADLINES[status] ?? `Request failed (HTTP ${status})`;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

// Pull a human-readable detail string from common backend error shapes
// (FastAPI/Litestar `detail`, Django `message`, plain string bodies).
const extractBackendDetail = (data: unknown): string | undefined => {
  const stringDetail = nonEmptyString(data);
  if (stringDetail) return stringDetail;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    return nonEmptyString(obj.detail) ?? nonEmptyString(obj.message) ?? nonEmptyString(obj.error);
  }
  return undefined;
};

// A TypeError mentioning JSON.parse, `.map is not a function`, or V8's
// "Cannot read properties of undefined (reading 'data'|'map')" is almost
// always a transformer-chain throw on an unexpected response shape — surface
// it as a neutral "Unable to load data" rather than leaking internals to the
// user. Kept narrow on purpose so genuine domain TypeErrors still surface.
const TRANSFORMER_SHAPE_ERROR_RE =
  /JSON\.parse|\.map is not a function|reading ['"](?:map|data)['"]/;

const isTransformerShapeError = (err: unknown): err is TypeError =>
  err instanceof TypeError && TRANSFORMER_SHAPE_ERROR_RE.test(err.message);

/**
 * Actions for sending alerts from XState actors
 *
 * These actions can be used in any XState machine to send alerts
 * directly to the alertView system actor.
 *
 * @example
 * ```ts
 * import { sendAlertAction } from '~/features/alerts/service-actions';
 *
 * // In your machine setup:
 * setup({
 *   actions: {
 *     sendAlert: sendAlertAction,
 *   },
 * })
 *
 * // In your machine:
 * on: {
 *   ON_ERROR: {
 *     actions: 'sendAlert',
 *   },
 * }
 * ```
 */

/**
 * Base scope for alert actions - provides access to the alertView system actor
 */
interface BaseAlertActionScope {
  system?: {
    get: (id: typeof ALERT_VIEW_STATE) => {
      send: (event: AlertEvents) => void;
    };
  };
}

/**
 * Scope for error alert actions - expects event with error payload
 */
interface ErrorAlertActionScope extends BaseAlertActionScope {
  event: {
    payload?: {
      text?: string;
      error?: ServiceError | Error | unknown;
    };
  };
}

/**
 * Scope for alert actions with custom text payload
 */
interface TextAlertActionScope extends BaseAlertActionScope {
  event: {
    payload?: {
      text?: string;
      message?: string;
    };
  };
}

/**
 * Scope for generic alert action with full AlertPayload
 */
interface AlertActionScope extends BaseAlertActionScope {
  event: {
    payload?: AlertPayload;
  };
}

/**
 * Send an alert to the alertView
 * Usage in machine: actions: ['sendAlert']
 *
 * @example
 * ```ts
 * // In your machine:
 * * on: {
 * *   SOME_EVENT: {
 * *     actions: sendAlertAction,
 * *   },
 * * }
 * ```
 */
export const sendAlertAction = ({ system }: AlertActionScope, event: AlertActionScope['event']) => {
  if (!system) {
    console.warn('[AlertView] System not available, cannot send alert');
    return;
  }

  const defaultPayload: AlertPayload = { text: 'Alert', type: 'info', timeout: 3000 };

  try {
    const alertView = system.get(ALERT_VIEW_STATE);
    alertView.send({
      type: 'ON_SHOW_ALERT',
      payload: event.payload || defaultPayload,
    });
  } catch (error) {
    console.warn('[AlertView] Could not send alert:', error);
  }
};

/**
 * Send an error alert
 * Usage in machine: actions: ['sendErrorAlert']
 *
 * @example
 * ```ts
 * // In your machine:
 * * onError: {
 * *   actions: sendErrorAlertAction,
 * * }
 * ```
 */
export const sendErrorAlertAction = ({ system, event }: ErrorAlertActionScope) => {
  if (!system) {
    console.warn('[AlertView] System not available, cannot send error alert');
    return;
  }

  const error = event?.payload?.error;
  const overrideText = event?.payload?.text;

  // Order matters: AxiosError instances carry a `code` field (e.g. `ERR_BAD_REQUEST`),
  // which would otherwise be caught by the ServiceError `'code' in error` check
  // below and rendered as the alert headline. Check axios first.

  // Handle axios errors with a real HTTP response — use status + backend
  // detail. Without this branch the user sees raw `error.message` like
  // "Request failed with status code 401", which doesn't tell them what to do.
  if (axios.isAxiosError(error) && error.response) {
    const status = error.response.status;
    // The auth middleware's rejection of a request that carried a Bearer token
    // (after the silent token refresh also failed) always gets the session copy;
    // a contextual overrideText would otherwise hide that the session needs a
    // refresh. A request sent without a token was never retried, so it keeps the
    // backend text and the override.
    const isAuthRejection = isMiddlewareAuthRejection(error) && requestSentBearerToken(error);
    const detail = extractBackendDetail(error.response.data);
    const text = isAuthRejection
      ? SESSION_REFRESH_TEXT
      : overrideText || formatHttpStatusHeadline(status);
    const subText = isAuthRejection ? SESSION_REFRESH_SUBTEXT : (detail ?? `HTTP ${status}`);

    const payload: AlertPayload = {
      text,
      subText,
      type: 'error',
      timeout: 5000,
    };

    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      alertView.send({ type: 'ON_SHOW_ALERT', payload });
    } catch (err) {
      console.warn('[AlertView] Could not send error alert:', err);
    }
    return;
  }

  // Handle network-level axios errors (no response — DNS, offline, CORS).
  if (axios.isAxiosError(error)) {
    const payload: AlertPayload = {
      text: overrideText || 'Network error',
      subText: 'Please check your connection and try again.',
      type: 'error',
      timeout: 5000,
    };

    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      alertView.send({ type: 'ON_SHOW_ALERT', payload });
    } catch (err) {
      console.warn('[AlertView] Could not send error alert:', err);
    }
    return;
  }

  // Clerk Core 3 distinguishes an offline token refresh from a signed-out
  // session by throwing ClerkOfflineError. Treat it as a recoverable network
  // condition; showing the raw `clerk_offline` service code would incorrectly
  // suggest that the user's authentication state is invalid.
  if (ClerkOfflineError.is(error)) {
    const payload: AlertPayload = {
      text: overrideText || 'You are offline',
      subText: 'Reconnect to the internet and try again.',
      type: 'error',
      timeout: 5000,
    };

    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      alertView.send({ type: 'ON_SHOW_ALERT', payload });
    } catch (err) {
      console.warn('[AlertView] Could not send error alert:', err);
    }
    return;
  }

  // Detect transformer-chain throws (e.g. `.map is not a function` on an
  // unexpected response body) and substitute a neutral message rather than
  // leaking internals to the user. Log the raw error so debugging is still
  // possible from the console.
  if (isTransformerShapeError(error)) {
    console.error('[AlertView] Transformer shape error suppressed:', error);
    const payload: AlertPayload = {
      text: overrideText || 'Unable to load data',
      subText: 'The server response was not in the expected format.',
      type: 'error',
      timeout: 5000,
    };

    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      alertView.send({ type: 'ON_SHOW_ALERT', payload });
    } catch (err) {
      console.warn('[AlertView] Could not send error alert:', err);
    }
    return;
  }

  // Handle ServiceError (a domain-specific error with code + message).
  // Must come after axios/transformer branches because AxiosError also has a
  // `code` field and would otherwise be misclassified here.
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    const serviceError = error as ServiceError;
    const payload: AlertPayload = {
      text: overrideText || serviceError.code,
      subText: serviceError.message,
      type: 'error',
      timeout: 5000,
      error: serviceError,
    };

    try {
      const alertView = system.get(ALERT_VIEW_STATE);
      alertView.send({ type: 'ON_SHOW_ALERT', payload });
    } catch (err) {
      console.warn('[AlertView] Could not send error alert:', err);
    }
    return;
  }

  // Handle regular Error
  const message = error instanceof Error ? error.message : 'An error occurred';
  const text = overrideText || 'Error';

  const payload: AlertPayload = {
    text,
    subText: message,
    type: 'error',
    timeout: 5000,
  };

  try {
    const alertView = system.get(ALERT_VIEW_STATE);
    alertView.send({ type: 'ON_SHOW_ALERT', payload });
  } catch (err) {
    console.warn('[AlertView] Could not send error alert:', err);
  }
};

/**
 * Send a success alert
 * Usage in machine: actions: ['sendSuccessAlert']
 *
 * @example
 * ```ts
 * // In your machine:
 * * on: {
 * *   ON_SUCCESS: {
 * *     actions: sendSuccessAlertAction,
 * *   },
 * * }
 * ```
 */
export const sendSuccessAlertAction = ({ system, event }: TextAlertActionScope) => {
  if (!system) {
    console.warn('[AlertView] System not available, cannot send success alert');
    return;
  }

  const text = event?.payload?.text || event?.payload?.message || 'Success';

  const payload: AlertPayload = {
    text,
    type: 'success',
    timeout: 3000,
  };

  try {
    const alertView = system.get(ALERT_VIEW_STATE);
    alertView.send({ type: 'ON_SHOW_ALERT', payload });
  } catch (err) {
    console.warn('[AlertView] Could not send success alert:', err);
  }
};

/**
 * Send a warning alert
 * Usage in machine: actions: ['sendWarningAlert']
 *
 * @example
 * ```ts
 * // In your machine:
 * * on: {
 * *   ON_WARNING: {
 * *     actions: sendWarningAlertAction,
 * *   },
 * * }
 * ```
 */
export const sendWarningAlertAction = ({ system, event }: TextAlertActionScope) => {
  if (!system) {
    console.warn('[AlertView] System not available, cannot send warning alert');
    return;
  }

  const text = event?.payload?.text || event?.payload?.message || 'Warning';

  const payload: AlertPayload = {
    text,
    type: 'warning',
    timeout: 5000,
  };

  try {
    const alertView = system.get(ALERT_VIEW_STATE);
    alertView.send({ type: 'ON_SHOW_ALERT', payload });
  } catch (err) {
    console.warn('[AlertView] Could not send warning alert:', err);
  }
};

/**
 * Send an info alert
 * Usage in machine: actions: ['sendInfoAlert']
 *
 * @example
 * ```ts
 * // In your machine:
 * * on: {
 * *   ON_INFO: {
 * *     actions: sendInfoAlertAction,
 * *   },
 * * }
 * ```
 */
export const sendInfoAlertAction = ({ system, event }: TextAlertActionScope) => {
  if (!system) {
    console.warn('[AlertView] System not available, cannot send info alert');
    return;
  }

  const text = event?.payload?.text || event?.payload?.message || 'Info';

  const payload: AlertPayload = {
    text,
    type: 'info',
    timeout: 5000,
  };

  try {
    const alertView = system.get(ALERT_VIEW_STATE);
    alertView.send({ type: 'ON_SHOW_ALERT', payload });
  } catch (err) {
    console.warn('[AlertView] Could not send info alert:', err);
  }
};
